const STORAGE_KEY = "qp_quetzalito_workspace";
const LEGACY_START_KEY = "qp_quetzalito_conversation_start";
const SESSIONS_KEY = "qp_quetzalito_conversation_sessions";
const ACTIVE_SESSION_KEY = "qp_quetzalito_active_conversation";
export const LEGACY_CONVERSATION_ID = "legacy";
export const QUETZALITO_DATA_UPDATED_EVENT = "quetzalito:datos-actualizados";

export interface QuetzalitoConversationSession {
  id: string;
  title: string;
  startedAt: string;
  pinned: boolean;
}

export interface QuetzalitoDataUpdatedDetail { workspaceId: string; }

function scopedKey(prefix: string, workspaceId: string): string {
  return `${prefix}:${workspaceId}`;
}

function readStoredWorkspaceId(): string | null {
  try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
}

function isSession(value: unknown): value is QuetzalitoConversationSession {
  if (!value || typeof value !== "object") return false;
  const item = value as Record<string, unknown>;
  return typeof item.id === "string" && typeof item.title === "string"
    && typeof item.startedAt === "string" && typeof item.pinned === "boolean";
}

function storeSessions(workspaceId: string, sessions: QuetzalitoConversationSession[]): void {
  try { localStorage.setItem(scopedKey(SESSIONS_KEY, workspaceId), JSON.stringify(sessions)); } catch { /* Estado local opcional. */ }
}

let activeWorkspaceId: string | null = readStoredWorkspaceId();

export function getActiveWorkspaceId(): string | null { return activeWorkspaceId; }

export function setActiveWorkspaceId(workspaceId: string | null): void {
  activeWorkspaceId = workspaceId;
  try {
    if (workspaceId) localStorage.setItem(STORAGE_KEY, workspaceId);
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* El estado continúa en memoria. */ }
}

export function clearWorkspaceLocalState(workspaceId: string): void {
  if (activeWorkspaceId === workspaceId) setActiveWorkspaceId(null);
  try {
    localStorage.removeItem(scopedKey(SESSIONS_KEY, workspaceId));
    localStorage.removeItem(scopedKey(ACTIVE_SESSION_KEY, workspaceId));
    localStorage.removeItem(scopedKey(LEGACY_START_KEY, workspaceId));
  } catch { /* El estado local es opcional y no bloquea el borrado remoto. */ }
}

export function getConversationSessions(workspaceId: string): QuetzalitoConversationSession[] {
  try {
    const stored = localStorage.getItem(scopedKey(SESSIONS_KEY, workspaceId));
    if (stored) {
      const parsed: unknown = JSON.parse(stored);
      if (Array.isArray(parsed)) return parsed.filter(isSession).sort((a, b) => a.startedAt.localeCompare(b.startedAt));
    }
    const legacyStart = localStorage.getItem(scopedKey(LEGACY_START_KEY, workspaceId));
    if (legacyStart) {
      const migrated: QuetzalitoConversationSession = {
        id: `chat_${legacyStart.replace(/\D/g, "") || Date.now()}`,
        title: "Conversación reciente",
        startedAt: legacyStart,
        pinned: false,
      };
      storeSessions(workspaceId, [migrated]);
      setActiveConversationId(workspaceId, migrated.id);
      localStorage.removeItem(scopedKey(LEGACY_START_KEY, workspaceId));
      return [migrated];
    }
  } catch { return []; }
  return [];
}

export function createConversationSession(workspaceId: string): QuetzalitoConversationSession {
  const sessions = getConversationSessions(workspaceId);
  const session: QuetzalitoConversationSession = {
    id: `chat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    title: `Conversación ${sessions.length + 2}`,
    startedAt: new Date().toISOString(),
    pinned: false,
  };
  storeSessions(workspaceId, [...sessions, session]);
  setActiveConversationId(workspaceId, session.id);
  return session;
}

export function updateConversationSession(workspaceId: string, sessionId: string, changes: Partial<Pick<QuetzalitoConversationSession, "title" | "pinned">>): void {
  storeSessions(workspaceId, getConversationSessions(workspaceId).map((session) => session.id === sessionId ? { ...session, ...changes } : session));
}

export function removeConversationSession(workspaceId: string, sessionId: string): void {
  storeSessions(workspaceId, getConversationSessions(workspaceId).filter((session) => session.id !== sessionId));
}

export function getActiveConversationId(workspaceId: string): string {
  try { return localStorage.getItem(scopedKey(ACTIVE_SESSION_KEY, workspaceId)) || LEGACY_CONVERSATION_ID; }
  catch { return LEGACY_CONVERSATION_ID; }
}

export function setActiveConversationId(workspaceId: string, sessionId: string): void {
  try { localStorage.setItem(scopedKey(ACTIVE_SESSION_KEY, workspaceId), sessionId); } catch { /* Estado local opcional. */ }
}

export function emitQuetzalitoDataUpdated(workspaceId: string): void {
  document.dispatchEvent(new CustomEvent<QuetzalitoDataUpdatedDetail>(QUETZALITO_DATA_UPDATED_EVENT, { detail: { workspaceId } }));
}
